import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  DOCUMENT,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  untracked,
  viewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { ToastComponent, ToastDefaults } from './toast';
import { ToastService } from './toast.service';
import { DEFAULT_TOASTER_CONFIG, TOASTER_CONFIG } from './toaster.config';
import { Toast, ToastId, ToastPosition } from './types';

interface ToastItem {
  toast: Toast;
  index: number;
  offset: number;
}

interface ToastGroup {
  position: ToastPosition;
  x: string;
  y: string;
  items: ToastItem[];
  frontHeight: number;
}

@Component({
  selector: 'wma-toaster',
  imports: [ToastComponent],
  templateUrl: './toaster.html',
  styleUrl: './toaster.css',
  // The styles target the toast elements, which are rendered by another component.
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown)': 'onDocumentKeydown($event)',
    '(document:pointerup)': 'onDocumentPointerUp($event)',
    '(document:visibilitychange)': 'documentHidden.set(document.hidden)',
  },
})
export class ToasterComponent {
  readonly #config = { ...DEFAULT_TOASTER_CONFIG, ...inject(TOASTER_CONFIG) };
  readonly #service = inject(ToastService);
  private readonly lists = viewChildren<ElementRef<HTMLOListElement>>('list');
  protected readonly document = inject(DOCUMENT);

  readonly position = input(this.#config.position);
  readonly expand = input(this.#config.expand);
  readonly visibleToasts = input(this.#config.visibleToasts);
  readonly duration = input(this.#config.duration);
  readonly richColors = input(this.#config.richColors);
  readonly closeButton = input(this.#config.closeButton);
  readonly gap = input(this.#config.gap);
  readonly offset = input(this.#config.offset);
  readonly mobileOffset = input(this.#config.mobileOffset);
  readonly hotkey = input(this.#config.hotkey);
  readonly pauseWhenPageIsHidden = input(this.#config.pauseWhenPageIsHidden);
  readonly containerAriaLabel = input(this.#config.containerAriaLabel);
  readonly closeButtonAriaLabel = input(this.#config.closeButtonAriaLabel);

  /** Position of the stack under the mouse. */
  protected readonly hovered = signal<ToastPosition | null>(null);
  /** Position of the stack containing the focus. */
  protected readonly focused = signal<ToastPosition | null>(null);
  protected readonly interacting = signal(false);
  protected readonly documentHidden = signal(this.document.hidden ?? false);
  readonly #heights = signal<ReadonlyMap<ToastId, number>>(new Map());
  /** Element focused before the focus entered the toasts, restored when the focus is lost. */
  #lastFocusedElement: HTMLElement | null = null;

  protected readonly defaults = computed<ToastDefaults>(() => ({
    duration: this.duration(),
    richColors: this.richColors(),
    closeButton: this.closeButton(),
    closeButtonAriaLabel: this.closeButtonAriaLabel(),
  }));

  protected readonly ariaLabel = computed(() => {
    const keys = this.hotkey().join('+').replace(/Key/g, '').replace(/Digit/g, '');
    return keys ? `${this.containerAriaLabel()} ${keys}` : this.containerAriaLabel();
  });

  protected readonly groups = computed<ToastGroup[]>(() => {
    const defaultPosition = this.position();
    const toasts = this.#service.toasts();
    const heights = this.#heights();
    const gap = this.gap();

    if (toasts.length === 0) {
      return [];
    }

    const positions = new Set<ToastPosition>([defaultPosition]);
    toasts.forEach((toast) => positions.add(toast.position ?? defaultPosition));

    return [...positions].map((position) => {
      const toastsAtPosition = toasts.filter((t) => (t.position ?? defaultPosition) === position);
      let heightBefore = 0;
      let toastsBefore = 0;

      const items = toastsAtPosition.map((toast, index) => {
        // Removed toasts keep their offset but no longer push the next ones.
        const offset = heightBefore + toastsBefore * gap;
        if (!toast.removed) {
          heightBefore += heights.get(toast.id) ?? 0;
          toastsBefore++;
        }
        return { toast, index, offset };
      });

      const front = toastsAtPosition.find((t) => !t.removed);
      const [y, x] = position.split('-');
      return {
        position,
        x,
        y,
        items,
        frontHeight: front ? (heights.get(front.id) ?? 0) : 0,
      };
    });
  });

  constructor() {
    // When the last toast leaves, the mouse events that would reset the state may never fire.
    effect(() => {
      if (this.#service.toasts().length === 0) {
        this.hovered.set(null);
        this.interacting.set(false);
      }
    });

    // Removing the focused toast drops the focus on the body, give it back to where it was.
    afterRenderEffect(() => {
      this.#service.toasts();
      const active = this.document.activeElement;
      if (untracked(this.focused) && (!active || active === this.document.body)) {
        this.focused.set(null);
        this.#restoreFocus();
      }
    });
  }

  protected isExpanded(position: ToastPosition): boolean {
    return this.expand() || this.hovered() === position || this.focused() === position;
  }

  protected isPaused(position: ToastPosition): boolean {
    return (
      this.hovered() === position ||
      this.focused() === position ||
      this.interacting() ||
      (this.pauseWhenPageIsHidden() && this.documentHidden())
    );
  }

  protected onHeightChange(id: ToastId, height: number): void {
    const ids = new Set(this.#service.toasts().map((t) => t.id));
    this.#heights.update((heights) => {
      const next = new Map([...heights].filter(([key]) => ids.has(key)));
      next.set(id, height);
      return next;
    });
  }

  protected onPointerDown(event: PointerEvent): void {
    const target = event.target as HTMLElement;
    if (target.closest('[data-dismissible="false"]')) {
      return;
    }
    this.interacting.set(true);
  }

  /** Releasing the pointer outside the toasts ends the hover that `mouseleave` kept during the interaction. */
  protected onDocumentPointerUp(event: PointerEvent): void {
    this.interacting.set(false);
    const target = event.target as Element | null;
    if (!target?.closest?.('[data-wma-toaster]')) {
      this.hovered.set(null);
    }
  }

  protected onFocusIn(event: FocusEvent, position: ToastPosition): void {
    const list = event.currentTarget as HTMLElement;
    const previous = event.relatedTarget as HTMLElement | null;
    if (!list.contains(previous)) {
      this.#lastFocusedElement = previous;
    }
    // Only keyboard focus expands the stack and pauses the timers: a click also focuses the
    // toast, and the focus would otherwise keep the toasts open until it moves elsewhere.
    this.focused.set(isFocusVisible(event.target as Element) ? position : null);
  }

  protected onFocusOut(event: FocusEvent): void {
    const list = event.currentTarget as HTMLElement;
    const next = event.relatedTarget as Node | null;
    if (list.contains(next)) {
      return;
    }
    const hadKeyboardFocus = this.focused() !== null;
    this.focused.set(null);
    if (!next && hadKeyboardFocus) {
      this.#restoreFocus();
    } else {
      this.#lastFocusedElement = null;
    }
  }

  #restoreFocus(): void {
    this.#lastFocusedElement?.focus({ preventScroll: true });
    this.#lastFocusedElement = null;
  }

  protected onDocumentKeydown(event: KeyboardEvent): void {
    const hotkey = this.hotkey();
    const matches =
      hotkey.length > 0 &&
      hotkey.every(
        (key) => (event as unknown as Record<string, unknown>)[key] === true || event.code === key,
      );

    if (matches) {
      event.preventDefault();
      this.lists()[0]?.nativeElement.focus();
      return;
    }

    if (event.key === 'Escape' && this.focused()) {
      // Move the focus out too, so the next focus on a toast is not mistaken for a return point.
      this.focused.set(null);
      if (this.#lastFocusedElement) {
        this.#restoreFocus();
      } else {
        (this.document.activeElement as HTMLElement | null)?.blur();
      }
    }
  }
}

function isFocusVisible(element: Element): boolean {
  try {
    return element.matches(':focus-visible');
  } catch {
    // Browsers without `:focus-visible` support.
    return true;
  }
}
